import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from "firebase/firestore";
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Avatar,
  Chip,
  Switch,
  Grid,
  Paper,
  
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import PersonIcon from "@mui/icons-material/Person";
import LocationOn from "@mui/icons-material/LocationOn";
import { db } from "../firebase";
import UserTable from "../components/UserTable";
import UserForm from "../components/UserForm";

/**
 * @param {object} [props.source] another country's customers (see utils/indiaOperationsSource.js);
 *   without it the page reads the Australian database as before.
 */
const Customers = ({ source = null }) => {
  const currencySymbol = source ? source.currencySymbol : "$";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsData, setDocsData] = useState(null);
  const [docsSubjectId, setDocsSubjectId] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileClient, setProfileClient] = useState(null);
  const [accountConfirmOpen, setAccountConfirmOpen] = useState(false);
  const [accountPendingValue, setAccountPendingValue] = useState(null);

  const normalizeStatusValue = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase();

  const isCustomerRoleDeactivated = (customer = {}) => {
    const safeCustomer = customer || {};
    const accountStatus = normalizeStatusValue(
      safeCustomer.accountStatus || safeCustomer.status,
    );
    const roleStatus = normalizeStatusValue(
      safeCustomer?.roleDeactivation?.customer?.status ||
        safeCustomer?.roleDeactivation?.user?.status,
    );

    return (
      safeCustomer.accountActive === false ||
      safeCustomer.inactive === true ||
      accountStatus === "deactivated" ||
      roleStatus === "deactivated"
    );
  };
 

  // Fetch customers from Firestore
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError("");

      if (source) {
        setCustomers(await source.listCustomers());
        return;
      }

      const q = query(
        collection(db, "users"),
        where("roles.customer", "==", true)
      );

      const querySnapshot = await getDocs(q);
      const customersData = [];

      querySnapshot.forEach((doc) => {
        customersData.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setCustomers(customersData);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError("Failed to fetch customers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // Helper to format different timestamp shapes
  const formatTimestamp = (dateField) => {
    if (!dateField) return "—";
    if (dateField.toDate && typeof dateField.toDate === "function") {
      try {
        return dateField.toDate().toLocaleString();
      } catch {
        return String(dateField);
      }
    }
    if (
      dateField.seconds !== undefined &&
      dateField.nanoseconds !== undefined
    ) {
      try {
        return new Date(
          dateField.seconds * 1000 + Math.floor(dateField.nanoseconds / 1000000)
        ).toLocaleString();
      } catch {
        return String(dateField);
      }
    }
    if (typeof dateField === "string") {
      try {
        const d = new Date(dateField);
        if (!isNaN(d)) return d.toLocaleString();
      } catch (e) {}
      return dateField;
    }
    return String(dateField);
  };

  const toEpochMillis = (dateField) => {
    if (!dateField) return 0;

    if (dateField.toDate && typeof dateField.toDate === "function") {
      try {
        return dateField.toDate().getTime();
      } catch {
        return 0;
      }
    }

    if (
      typeof dateField === "object" &&
      dateField.seconds !== undefined &&
      dateField.nanoseconds !== undefined
    ) {
      return dateField.seconds * 1000 + Math.floor(dateField.nanoseconds / 1000000);
    }

    if (dateField instanceof Date) {
      return dateField.getTime();
    }

    if (typeof dateField === "number") {
      return Number.isFinite(dateField) ? dateField : 0;
    }

    if (typeof dateField === "string") {
      const parsed = new Date(dateField);
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    }

    return 0;
  };

  // Handle create customer
  const handleCreate = () => {
    setEditingCustomer(null);
    setFormOpen(true);
  };

  // Handle delete customer
  const handleDelete = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  // Handle view documents (similar to Clients.js)
  const handleViewDocs = async (customer) => {
    if (!customer || !customer.id) return;
    setDocsDialogOpen(true);
    setDocsLoading(true);
    setDocsData(null);
    setDocsSubjectId(customer.id);

    const userId = customer.id;
    const result = { topLevel: null, subcollections: {} };

    try {
      try {
        const userSnap = await getDoc(doc(db, "users", userId));
        if (userSnap && userSnap.exists()) result.topLevel = userSnap.data();
      } catch (err) {
        console.warn("Failed to fetch top-level user doc for docs view", err);
      }

      const candidateSubcollections = [
        "providerDetails",
        "provider_details",
        "onboarding",
        "onboardingDetails",
        "onboarding_details",
        "details",
        "provider_onboarding",
        "documents",
        "onboarding_documents",
      ];

      for (const colName of candidateSubcollections) {
        try {
          const cRef = collection(db, "users", userId, colName);
          const snaps = await getDocs(cRef);
          if (!snaps.empty) {
            result.subcollections[colName] = snaps.docs.map((d) => ({
              id: d.id,
              data: d.data(),
            }));
          }
        } catch (err) {
          // ignore missing permissions/collections
        }
      }

      // Try details nested candidates
      try {
        const detailsRef = collection(db, "users", userId, "details");
        const detailsSnap = await getDocs(detailsRef);
        if (!detailsSnap.empty) {
          const nestedCandidates = [
            "documents",
            "files",
            "attachments",
            "onboarding_documents",
            "profile",
            "photos",
          ];
          for (const d of detailsSnap.docs) {
            for (const subName of nestedCandidates) {
              try {
                const nestedRef = collection(
                  db,
                  "users",
                  userId,
                  "details",
                  d.id,
                  subName
                );
                const nestedSnap = await getDocs(nestedRef);
                if (!nestedSnap.empty) {
                  const key = `details/${d.id}/${subName}`;
                  result.subcollections[key] = nestedSnap.docs.map((nd) => ({
                    id: nd.id,
                    data: nd.data(),
                  }));
                }
              } catch (err) {
                // ignore missing nested collections
              }
            }
          }
        }
      } catch (err) {
        // ignore
      }

      setDocsData(result);
    } catch (err) {
      console.error("Failed to load documents for user", userId, err);
      setDocsData(result);
    } finally {
      setDocsLoading(false);
    }
  };

  // Adds the service history and orders shown in the profile dialog.
  const withBookingHistory = (merged, bookingList) => {
    const bookings = [...bookingList].sort((a, b) => {
      const aTime = toEpochMillis(
        a.createdAt ||
          a.updatedAt ||
          a.acceptanceCreatedAt ||
          a.scheduledDate ||
          a.bookingDate ||
          a.selectedDate ||
          a.date
      );
      const bTime = toEpochMillis(
        b.createdAt ||
          b.updatedAt ||
          b.acceptanceCreatedAt ||
          b.scheduledDate ||
          b.bookingDate ||
          b.selectedDate ||
          b.date
      );
      return bTime - aTime;
    });

    if (bookings.length > 0) {
      merged.serviceHistory = bookings.map((booking, index) => ({
        id: booking.id,
        title:
          booking.serviceName ||
          booking.subcategory ||
          booking.leadSubcategory ||
          booking.category ||
          booking.leadCategory ||
          booking.title ||
          `Service #${index + 1}`,
        serviceName:
          booking.serviceName ||
          booking.subcategory ||
          booking.leadSubcategory ||
          booking.category ||
          booking.leadCategory,
        date:
          booking.scheduledDate ||
          booking.bookingDate ||
          booking.selectedDate ||
          booking.date ||
          booking.createdAt,
        createdAt: booking.createdAt || booking.updatedAt,
        status: booking.status || booking.bookingStatus,
      }));

      merged.orders = bookings.map((booking) => {
        const resolvedTotal =
          booking.totalAmount ??
          booking.total ??
          booking.amount ??
          booking.price ??
          booking.servicePrice ??
          booking.payment?.total ??
          null;

        return {
          ...booking,
          id: booking.id,
          orderNumber: booking.orderNumber || booking.bookingId || booking.id,
          total: resolvedTotal,
          amount: booking.amount ?? resolvedTotal,
        };
      });
    }
    return merged;
  };

  // Open profile and merge details subcollection
  const handleViewProfile = async (customer) => {
    if (!customer || !customer.id) return;
    setProfileDialogOpen(true);
    setProfileClient(null);

    try {
      if (source) {
        const { customer: profile, bookings } = await source.getCustomerProfile(customer.id);
        setProfileClient(withBookingHistory({ ...customer, ...profile }, bookings));
        return;
      }

      const detailsCol = collection(db, "users", customer.id, "details");
      const detailsSnap = await getDocs(detailsCol);
      const detailsMap = {};
      const nestedCandidates = [
        "documents",
        "files",
        "attachments",
        "onboarding_documents",
        "profile",
        "photos",
      ];
      for (const d of detailsSnap.docs) {
        const data = d.data();
        for (const subName of nestedCandidates) {
          try {
            const nestedRef = collection(
              db,
              "users",
              customer.id,
              "details",
              d.id,
              subName
            );
            const nestedSnap = await getDocs(nestedRef);
            if (!nestedSnap.empty) {
              data[subName] = nestedSnap.docs.map((nd) => ({
                id: nd.id,
                data: nd.data(),
              }));
            }
          } catch (err) {
            // ignore
          }
        }
        detailsMap[d.id] = data;
      }

      const merged = { ...customer, details: detailsMap };

      for (const [docId, docData] of Object.entries(detailsMap)) {
        merged[docId] = docData;
      }

      const findDetailsDoc = (candidates) => {
        for (const name of candidates) {
          if (detailsMap[name]) return detailsMap[name];
          if (merged[name]) return merged[name];
        }
        return null;
      };

      const bankingCandidates = [
        "bankingDetails",
        "banking",
        "bank_details",
        "banking_details",
      ];
      const businessCandidates = [
        "businessInformation",
        "business_info",
        "businessInformation",
        "businessInformation",
      ];
      const profileCandidates = ["profile", "profileInfo", "profile_info"];
      const onboardingCandidates = [
        "provider_onboarding",
        "providerOnboarding",
        "onboarding",
        "onboarding_details",
        "onboardingDetails",
      ];

      const bankingDoc = findDetailsDoc(bankingCandidates);
      const businessDoc = findDetailsDoc(businessCandidates);
      const profileDoc = findDetailsDoc(profileCandidates);
      const onboardingDoc =
        findDetailsDoc(onboardingCandidates) ||
        detailsMap.provider_onboarding ||
        detailsMap.onboarding ||
        null;

      if (bankingDoc) merged.bankingDetails = bankingDoc;
      if (businessDoc) merged.businessInformation = businessDoc;
      if (profileDoc)
        merged.profile = { ...(merged.profile || {}), ...profileDoc };
      if (onboardingDoc) merged.provider_onboarding = onboardingDoc;

      // If provider_onboarding contains nested sections, copy them to merged for easier access
      if (merged.provider_onboarding) {
        if (merged.provider_onboarding.bankingDetails) {
          merged.bankingDetails =
            merged.bankingDetails || merged.provider_onboarding.bankingDetails;
        }
        if (merged.provider_onboarding.businessInformation) {
          merged.businessInformation =
            merged.businessInformation ||
            merged.provider_onboarding.businessInformation;
        }
        if (merged.provider_onboarding.profile) {
          merged.profile = {
            ...(merged.profile || {}),
            ...merged.provider_onboarding.profile,
          };
        }
        if (merged.provider_onboarding.documents) {
          merged.documents =
            merged.documents || merged.provider_onboarding.documents;
        }
      }

      if (merged.bankingDetails) {
        merged.bankName =
          merged.bankName ||
          merged.bankingDetails.bankName ||
          merged.bankingDetails.bank_name ||
          merged.bankingDetails.bank;
        merged.accountName =
          merged.accountName ||
          merged.bankingDetails.accountName ||
          merged.bankingDetails.account_name ||
          merged.bankingDetails.account;
        merged.bsb =
          merged.bsb ||
          merged.bankingDetails.bsb ||
          merged.bankingDetails.bsbNumber;
        merged.accountNumber =
          merged.accountNumber ||
          merged.bankingDetails.accountNumber ||
          merged.bankingDetails.account_number;
      }

      if (merged.businessInformation) {
        merged.abn =
          merged.abn ||
          merged.businessInformation.abnNumber ||
          merged.businessInformation.abn_number ||
          merged.businessInformation.abn;
        merged.tfn =
          merged.tfn ||
          merged.businessInformation.tfnNumber ||
          merged.businessInformation.tfn_number ||
          merged.businessInformation.tfn;
        merged.businessName =
          merged.businessName ||
          merged.businessInformation.businessName ||
          merged.businessInformation.business_name;
      }

      if (merged.profile) {
        merged.nationalityStatus =
          merged.nationalityStatus ||
          merged.profile.nationalityStatus ||
          merged.profile.nationality_status;
        merged.preferredGender =
          merged.preferredGender ||
          merged.profile.preferredGender ||
          merged.profile.preferred_gender;
        if (
          (!merged.servicesOffered || merged.servicesOffered.length === 0) &&
          merged.profile.servicesOffered
        ) {
          merged.servicesOffered = Array.isArray(merged.profile.servicesOffered)
            ? merged.profile.servicesOffered
            : String(merged.profile.servicesOffered)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
        }
      }

      if (merged.provider_onboarding) {
        merged.visaCategory =
          merged.visaCategory ||
          merged.provider_onboarding.visaCategory ||
          merged.provider_onboarding.visa_category ||
          merged.provider_onboarding.visaType;
        if (merged.provider_onboarding.visaExpiry)
          merged.visaExpiry =
            merged.visaExpiry || merged.provider_onboarding.visaExpiry;
        if (
          merged.provider_onboarding.servicesOffered &&
          (!merged.servicesOffered || merged.servicesOffered.length === 0)
        ) {
          merged.servicesOffered = Array.isArray(
            merged.provider_onboarding.servicesOffered
          )
            ? merged.provider_onboarding.servicesOffered
            : String(merged.provider_onboarding.servicesOffered)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
        }
      }

      if (merged.bankingDetails) {
        merged.bankName = merged.bankingDetails.bankName || merged.bankName;
        merged.accountName =
          merged.bankingDetails.accountName || merged.accountName;
        merged.bsb = merged.bankingDetails.bsb || merged.bsb;
        merged.accountNumber =
          merged.bankingDetails.accountNumber || merged.accountNumber;
      }
      if (merged.businessInformation) {
        merged.abn = merged.businessInformation.abnNumber || merged.abn;
        merged.tfn = merged.businessInformation.tfnNumber || merged.tfn;
      }
      if (merged.profile) {
        merged.nationalityStatus =
          merged.profile.nationalityStatus || merged.nationalityStatus;
        merged.preferredGender =
          merged.profile.preferredGender || merged.preferredGender;
        if (!merged.servicesOffered || merged.servicesOffered.length === 0)
          merged.servicesOffered =
            merged.profile.servicesOffered || merged.servicesOffered;
      }

      const bookingQueries = [
        query(collection(db, "bookings"), where("customerId", "==", customer.id)),
        query(collection(db, "bookings"), where("customer_id", "==", customer.id)),
        query(collection(db, "bookings"), where("userId", "==", customer.id)),
      ];

      const bookingsMap = new Map();
      for (const bookingsQuery of bookingQueries) {
        try {
          const snapshot = await getDocs(bookingsQuery);
          snapshot.forEach((bookingDoc) => {
            if (!bookingsMap.has(bookingDoc.id)) {
              bookingsMap.set(bookingDoc.id, {
                id: bookingDoc.id,
                ...bookingDoc.data(),
              });
            }
          });
        } catch (bookingErr) {
          console.warn(
            "Could not fetch customer bookings using one of the fallback query fields",
            bookingErr
          );
        }
      }

      withBookingHistory(merged, Array.from(bookingsMap.values()));

      setProfileClient(merged);
    } catch (err) {
      console.error(
        "Failed to load profile details for user",
        customer.id,
        err
      );
      setProfileClient(customer);
    }
  };

  // Confirm delete with cascade deletion
  const confirmDelete = async () => {
    if (!customerToDelete) return;

    try {
      setFormLoading(true);
      const customerId = customerToDelete.id;

      // Delete all bookings made by this customer
      const bookingsQuery = query(
        collection(db, "bookings"),
        where("customerId", "==", customerId)
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingDeletePromises = bookingsSnapshot.docs.map((bookingDoc) =>
        deleteDoc(doc(db, "bookings", bookingDoc.id))
      );
      await Promise.all(bookingDeletePromises);

      // Delete any reviews/ratings by this customer (if you have such collection)
      const reviewsQuery = query(
        collection(db, "reviews"),
        where("customerId", "==", customerId)
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const reviewDeletePromises = reviewsSnapshot.docs.map((reviewDoc) =>
        deleteDoc(doc(db, "reviews", reviewDoc.id))
      );
      await Promise.all(reviewDeletePromises);

      // Delete any payment records (if you have such collection)
      const paymentsQuery = query(
        collection(db, "payments"),
        where("customerId", "==", customerId)
      );
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const paymentDeletePromises = paymentsSnapshot.docs.map((paymentDoc) =>
        deleteDoc(doc(db, "payments", paymentDoc.id))
      );
      await Promise.all(paymentDeletePromises);

      // Mark the user document for deletion and disable account
      await updateDoc(doc(db, "users", customerId), {
        deleted: true,
        deletedAt: Timestamp.now(),
        disabled: true,
        email: `deleted_${customerId}@deleted.com`, // Change email to prevent login
      });

      // Delete the customer user document from Firestore
      // This will automatically trigger the Cloud Function to delete the auth account
      await deleteDoc(doc(db, "users", customerId));

      setCustomers((prev) =>
        prev.filter((customer) => customer.id !== customerId)
      );

      showSnackbar(
        `Customer deleted successfully! Removed ${bookingsSnapshot.size} bookings, ${reviewsSnapshot.size} reviews, and ${paymentsSnapshot.size} payments. Authentication account will be automatically deleted.`,
        "success"
      );
    } catch (err) {
      console.error("Error deleting customer and related data:", err);
      showSnackbar("Failed to delete customer. Please try again.", "error");
    } finally {
      setFormLoading(false);
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);

      // Prepare data for Firestore
      const userData = {
        ...formData,
        dob: formData.dob ? Timestamp.fromDate(formData.dob) : null,
        updatedAt: Timestamp.now(),
      };

      if (editingCustomer) {
        // Update existing customer
        await updateDoc(doc(db, "users", editingCustomer.id), userData);

        // Update local state
        setCustomers((prev) =>
          prev.map((customer) =>
            customer.id === editingCustomer.id
              ? { ...customer, ...userData }
              : customer
          )
        );

        showSnackbar("Customer updated successfully", "success");
      } else {
        // Create new customer
        userData.createdAt = Timestamp.now();
        const docRef = await addDoc(collection(db, "users"), userData);

        // Add to local state
        setCustomers((prev) => [...prev, { id: docRef.id, ...userData }]);

        showSnackbar("Customer created successfully", "success");
      }

      setFormOpen(false);
      setEditingCustomer(null);
    } catch (err) {
      console.error("Error saving customer:", err);
      showSnackbar("Failed to save customer", "error");
    } finally {
      setFormLoading(false);
    }
  };

  // Show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  // Handle account status toggle (activate/deactivate customer role)
  const handleAccountStatusToggle = async (checked) => {
    if (!profileClient?.id) return;

    try {
      const isActive = checked;
      const userRef = doc(db, "users", profileClient.id);
      const nowIso = new Date().toISOString();
      const roleDeactivation =
        profileClient?.roleDeactivation &&
        typeof profileClient.roleDeactivation === "object"
          ? profileClient.roleDeactivation
          : {};
      const existingRoleRecord = roleDeactivation.customer || {};
      const nextRoleRecord = isActive
        ? {
            ...existingRoleRecord,
            status: "active",
            reactivatedAt: nowIso,
            reactivatedBy: "admin",
            updatedAt: nowIso,
          }
        : {
            ...existingRoleRecord,
            status: "deactivated",
            deactivatedAt: nowIso,
            deactivatedBy: "admin",
            reason: "Customer role deactivated by admin",
            updatedAt: nowIso,
          };

      if (isActive) {
        delete nextRoleRecord.reason;
      }

      let nextRoleDeactivation = {
        ...roleDeactivation,
        customer: nextRoleRecord,
      };

      if (source) {
        // The server writes the same record (and refuses bookings while deactivated).
        nextRoleDeactivation = (await source.setCustomerActive(profileClient.id, isActive))
          .roleDeactivation;
      } else {
        await updateDoc(userRef, {
          roleDeactivation: nextRoleDeactivation,
          updatedAt: nowIso,
        });
      }

      setProfileClient((prev) => ({
        ...prev,
        roleDeactivation: nextRoleDeactivation,
      }));

      setCustomers((prev) =>
        prev.map((customer) =>
          customer.id === profileClient.id
            ? {
                ...customer,
                roleDeactivation: nextRoleDeactivation,
              }
            : customer,
        ),
      );

      showSnackbar(
        `Customer role ${isActive ? "activated" : "deactivated"} successfully`,
        "success",
      );
    } catch (err) {
      console.error("Error updating customer account status:", err);
      showSnackbar("Failed to update account status", "error");
    }
  };

  // Called when admin toggles the switch in the UI - show confirmation first
  const handleAccountToggleRequest = (checked) => {
    setAccountPendingValue(checked);
    setAccountConfirmOpen(true);
  };

  const handleConfirmAccountToggle = async () => {
    setAccountConfirmOpen(false);
    if (accountPendingValue === null) return;
    await handleAccountStatusToggle(accountPendingValue);
    setAccountPendingValue(null);
  };

  const customerAccountDeactivated = isCustomerRoleDeactivated(profileClient);

  return (
    <Box>
      {/* <Typography variant="h4" gutterBottom>
        Customer Management
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage all customers in your system. You can create, edit, and delete customer records.
      </Typography> */}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <UserTable
        users={customers}
        onDelete={handleDelete}
        onCreate={handleCreate}
        onViewDocs={handleViewDocs}
        onView={handleViewProfile}
        loading={loading}
      />

      {/* Customer Form Dialog */}
      <UserForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingCustomer(null);
        }}
        onSubmit={handleFormSubmit}
        user={editingCustomer}
        userType="customer"
        loading={formLoading}
      />

      {/* Profile Dialog (show merged details) */}
      <Dialog
        open={profileDialogOpen}
        onClose={() => {
          setProfileDialogOpen(false);
          setProfileClient(null);
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => {
                setProfileDialogOpen(false);
                setProfileClient(null);
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </DialogTitle>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              sx={{ width: 64, height: 64 }}
              src={profileClient?.photoURL}
            >
              <PersonIcon />
            </Avatar>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {profileClient?.name || "Unknown"}
              </Typography>
              <Box
                sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}
              >
                {profileClient?.onboardingDocumentsVerified && (
                  <Chip label="Verified" size="small" color="success" />
                )}
                {customerAccountDeactivated ? (
                  <Chip label="Account Deactivated" size="small" color="error" />
                ) : (
                  <Chip label="Account Active" size="small" color="success" />
                )}
                {profileClient?.activeRole && (
                  <Chip label="Customer" size="small" />
                )}
                {/* {approvalStatus && (
                    <Chip
                      label={
                        typeof approvalStatus === 'string'
                          ? approvalStatus.charAt(0).toUpperCase() + approvalStatus.slice(1)
                          : String(approvalStatus)
                      }
                      size="small"
                      color={
                        ('' + approvalStatus).toLowerCase() === 'approved'
                          ? 'success'
                          : ('' + approvalStatus).toLowerCase() === 'pending'
                          ? 'warning'
                          : ('' + approvalStatus).toLowerCase() === 'rejected'
                          ? 'error'
                          : 'default'
                      }
                    />
                  )} */}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Account Status:
              </Typography>
              <Switch
                checked={!customerAccountDeactivated}
                onChange={(e) => handleAccountToggleRequest(e.target.checked)}
                color={!customerAccountDeactivated ? "success" : "default"}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: !customerAccountDeactivated
                    ? "success.main"
                    : "error.main",
                }}
              >
                {!customerAccountDeactivated ? "Active" : "Deactivated"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {profileClient ? (
            <>
              <Grid container spacing={2} alignItems="stretch">
                <Grid item size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex" }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <PersonIcon sx={{ fontSize: 20 }} />
                      <Typography variant="h6">Personal Information</Typography>
                    </Box>
                    <Box sx={{ mt: 2, display: "grid", gap: 1.25, flex: 1 }}>
                      <Box>
                        <Typography variant="body2">Full Name</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {profileClient?.name || "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">Email</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {profileClient?.email || "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">Phone</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {profileClient?.phone || "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">Joined</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {profileClient?.createdAt || profileClient?.created_at || profileClient?.createdOn
                            ? formatTimestamp(profileClient.createdAt || profileClient.created_at || profileClient.createdOn)
                            : "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex" }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <LocationOn sx={{ fontSize: 20 }} />
                      <Typography variant="h6">Address Information</Typography>
                    </Box>
                    <Box sx={{ mt: 2, display: "grid", gap: 1.25, flex: 1 }}>
                      <Box>
                        <Typography variant="body2">Address</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {profileClient?.formattedAddress ||
                            profileClient?.address ||
                            "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">City / Suburb</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {profileClient?.city ||
                            profileClient?.address?.city ||
                            "—"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2">Postcode</Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {profileClient?.postcode ||
                            profileClient?.address?.postcode ||
                            "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: "flex" }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h6">Service History</Typography>
                    <Box sx={{ mt: 2 }}>
                      {Array.isArray(profileClient?.serviceHistory) &&
                      profileClient.serviceHistory.length ? (
                        <Box sx={{ display: "grid", gap: 1 }}>
                          {profileClient.serviceHistory
                            .slice(0, 6)
                            .map((h, i) => (
                              <Box
                                key={i}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography>
                                  {h.title ||
                                    h.serviceName ||
                                    `Service #${i + 1}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {h.date
                                    ? new Date(h.date).toLocaleDateString()
                                    : h.createdAt
                                    ? formatTimestamp(h.createdAt)
                                    : "—"}
                                </Typography>
                              </Box>
                            ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No service history available.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mt: 3 }}>
                <Grid item size={{ xs: 12}} sx={{ display: "flex" }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h6">Recent Orders</Typography>
                    <Box sx={{ mt: 2 }}>
                      {Array.isArray(profileClient?.orders) &&
                      profileClient.orders.length ? (
                        <Box sx={{ display: "grid", gap: 1 }}>
                          {profileClient.orders.slice(0, 6).map((o, i) => (
                            <Box
                              key={i}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography>
                                {o.orderNumber || `Order #${o.id || i + 1}`}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {o.total
                                  ? `${currencySymbol}${o.total}`
                                  : o.amount
                                  ? `${currencySymbol}${o.amount}`
                                  : "—"}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No recent orders.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <Typography>Loading profile...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setProfileDialogOpen(false);
              setProfileClient(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog
        open={docsDialogOpen}
        onClose={() => setDocsDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>User Documents</DialogTitle>
        <DialogContent dividers>
          {docsLoading ? (
            <div>Loading...</div>
          ) : docsData ? (
            <div>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Top-level user data
              </Typography>
              <pre style={{ maxHeight: 160, overflow: "auto" }}>
                {JSON.stringify(docsData.topLevel || {}, null, 2)}
              </pre>

              {Object.keys(docsData.subcollections || {}).length === 0 ? (
                <Typography sx={{ mt: 2 }}>
                  No onboarding/details subcollections found.
                </Typography>
              ) : (
                Object.entries(docsData.subcollections).map(
                  ([colName, docs]) => (
                    <div key={colName} style={{ marginTop: 12 }}>
                      <Typography variant="subtitle2">{colName}</Typography>
                      {docs.map((d) => (
                        <div key={d.id} style={{ marginTop: 8 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {d.id}
                          </Typography>
                          <pre style={{ maxHeight: 140, overflow: "auto" }}>
                            {JSON.stringify(d.data, null, 2)}
                          </pre>
                          {d.data &&
                            typeof d.data === "object" &&
                            Object.values(d.data).map((v, i) =>
                              typeof v === "string" &&
                              (v.startsWith("http") ||
                                v.includes("firebase")) ? (
                                <Button
                                  key={i}
                                  size="small"
                                  href={v}
                                  target="_blank"
                                  rel="noreferrer"
                                  sx={{ mr: 1 }}
                                >
                                  Open file
                                </Button>
                              ) : null
                            )}
                        </div>
                      ))}
                    </div>
                  )
                )
              )}
            </div>
          ) : (
            <Typography>No data found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocsDialogOpen(false)}>Close</Button>
          <Button
            onClick={async () => {
              if (!docsData || !docsData.topLevel) return;
              try {
                setFormLoading(true);
                const subjectId =
                  (docsData.topLevel && docsData.topLevel.uid) ||
                  (docsData.topLevel && docsData.topLevel.id) ||
                  null;
                const userIdToUpdate = subjectId || docsSubjectId || null;
                if (!userIdToUpdate) {
                  throw new Error("Unable to determine user id to verify");
                }
                await updateDoc(doc(db, "users", userIdToUpdate), {
                  onboardingDocumentsVerified: true,
                  documentsVerifiedAt: new Date().toISOString(),
                });
                showSnackbar("Documents marked as verified", "success");
                setDocsDialogOpen(false);
              } catch (err) {
                console.error("Failed to verify documents:", err);
                showSnackbar("Failed to verify documents", "error");
              } finally {
                setFormLoading(false);
              }
            }}
            variant="contained"
          >
            Mark as Verified
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for activate/deactivate */}
      <Dialog
        open={accountConfirmOpen}
        onClose={() => {
          setAccountConfirmOpen(false);
          setAccountPendingValue(null);
        }}
        aria-labelledby="customer-account-confirm-dialog-title"
      >
        <DialogTitle id="customer-account-confirm-dialog-title">
          Confirm action
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {accountPendingValue
              ? "Are you sure you want to activate this customer role?"
              : "Are you sure you want to deactivate this customer role?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAccountConfirmOpen(false);
              setAccountPendingValue(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAccountToggle}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete Customer
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete {customerToDelete?.name}?
            <br />
            <br />
            <strong>Warning:</strong> This will permanently delete:
            <ul>
              <li>All bookings made by this customer</li>
              <li>All reviews and ratings by this customer</li>
              <li>All payment records</li>
              <li>The customer's Firestore data</li>
            </ul>
            <strong>Note:</strong> The Firebase Authentication account (UID:{" "}
            {customerToDelete?.id?.substring(0, 8)}...) will remain and should
            be manually deleted from Firebase Console → Authentication → Users.
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={formLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            autoFocus
            color="error"
            disabled={formLoading}
          >
            {formLoading ? "Deleting..." : "Delete All"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Customers;
